import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { ActivityLogsBuilder } from '@skills/activity-logs.jsx';
import { isValidAdminToken } from '@generated/server/adminAuth';

export const MONITORED_BOARDS = [
  { id: '5105003239', name: 'קליטת פוסט-דוקטורט (External_Postdoc_onboarding)', code: 'postdoc' },
  { id: '5104746820', name: 'טופס חיצוני לפוסט בידינג', code: 'postbidding_ext' },
  { id: '5104802965', name: 'לוח בקשות פוסט בידינג', code: 'postbidding' },
  { id: '5104462931', name: 'טופס הצהרת מילואים חיצוני', code: 'military' },
] as const;

export interface NormalizedSecurityLog {
  id: string;
  boardId: string;
  boardName: string;
  event: string;
  eventCategory: 'submission' | 'security' | 'status_update' | 'system';
  eventTitle: string;
  description: string;
  pulseId?: string;
  pulseName?: string;
  columnTitle?: string;
  oldValue?: string;
  newValue?: string;
  userId?: string;
  createdAt: string;
}

export interface ActivityPatternStats {
  totalSubmissions: number;
  totalSecurityEvents: number;
  totalStatusUpdates: number;
  totalEvents: number;
  submissionsByBoard: Record<string, number>;
  submissionsByDay: { date: string; count: number }[];
}

export interface SecurityActivityLogsResponse {
  logs: NormalizedSecurityLog[];
  stats: ActivityPatternStats;
  scannedBoardsCount: number;
  error?: string | null;
}

function parseColumnValue(val: unknown): string {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (typeof val === 'object') {
    const obj = val as Record<string, unknown>;
    if (obj.label && typeof obj.label === 'string') return obj.label;
    if (obj.text && typeof obj.text === 'string') return obj.text;
    if (obj.email && typeof obj.email === 'string') return obj.email;
    if (obj.name && typeof obj.name === 'string') return obj.name;
    return JSON.stringify(val);
  }
  return String(val);
}

function normalizeLog(log: any, boardId: string, boardName: string): NormalizedSecurityLog {
  const event = log.event || 'unknown';
  const data = log.data || {};
  let eventCategory: 'submission' | 'security' | 'status_update' | 'system' = 'system';
  let eventTitle = 'פעילות מערכת';
  let description = 'בוצעה פעולה בלוח';

  const pulseId = data.pulse_id ? String(data.pulse_id) : undefined;
  const pulseName = data.pulse_name || (data.value && typeof data.value === 'object' ? data.value.name : undefined) || 'רשומה';

  if (event === 'create_pulse') {
    eventCategory = 'submission';
    eventTitle = 'הגשת טופס חדש';
    description = `נקלטה הגשה חדשה במערכת: "${pulseName}"`;
  } else if (event === 'update_column_value') {
    const colTitle = data.column_title || data.column_id || 'שדה';
    const oldVal = parseColumnValue(data.previous_value);
    const newVal = parseColumnValue(data.value);

    // Check if security / OTP / signature related
    if (
      String(data.column_id).includes('column59') ||
      String(data.column_id).includes('column60') ||
      String(data.column_id).includes('column19') ||
      String(data.column_id).includes('column20') ||
      String(colTitle).includes('אימות') ||
      String(colTitle).includes('קוד')
    ) {
      eventCategory = 'security';
      eventTitle = 'אימות אבטחה וקוד OTP';
      description = `פעולת אימות בשדה ${colTitle}: ${newVal}`;
    } else if (
      String(colTitle).includes('סטטוס') ||
      String(colTitle).includes('שלב') ||
      String(colTitle).includes('אישור') ||
      String(colTitle).includes('Approval') ||
      String(colTitle).includes('Status')
    ) {
      eventCategory = 'status_update';
      eventTitle = `עדכון סטטוס: ${colTitle}`;
      description = `הסטטוס עודכן מ-"${oldVal}" ל-"${newVal}" ברשומה "${pulseName}"`;
    } else {
      eventCategory = 'system';
      eventTitle = `עדכון שדה: ${colTitle}`;
      description = `עודכן שדה ${colTitle} ברשומה "${pulseName}"`;
    }

    return {
      id: String(log.id),
      boardId,
      boardName,
      event,
      eventCategory,
      eventTitle,
      description,
      pulseId,
      pulseName,
      columnTitle: colTitle,
      oldValue: oldVal,
      newValue: newVal,
      userId: log.userId,
      createdAt: log.createdAt instanceof Date ? log.createdAt.toISOString() : new Date().toISOString(),
    };
  } else if (event === 'archive_pulse' || event === 'delete_pulse') {
    eventCategory = 'security';
    eventTitle = 'ארכוב / ביטול בקשה';
    description = `רשומה אורכבה או נמחקה: "${pulseName}"`;
  } else {
    eventTitle = event;
    description = `אירוע ${event} ברשומה "${pulseName}"`;
  }

  return {
    id: String(log.id),
    boardId,
    boardName,
    event,
    eventCategory,
    eventTitle,
    description,
    pulseId,
    pulseName,
    userId: log.userId,
    createdAt: log.createdAt instanceof Date ? log.createdAt.toISOString() : new Date().toISOString(),
  };
}

export const getSecurityActivityLogs = createServerFn({ method: 'GET' })
  .validator(
    z.object({
      adminToken: z.string().min(1),
      boardId: z.string().optional(),
      days: z.number().int().min(1).max(90).default(30),
      limit: z.number().int().min(5).max(100).default(50),
    }),
  )
  .handler(async ({ data }): Promise<SecurityActivityLogsResponse> => {
    // Validate Admin Token
    if (!isValidAdminToken(data.adminToken)) {
      return {
        logs: [],
        stats: {
          totalSubmissions: 0,
          totalSecurityEvents: 0,
          totalStatusUpdates: 0,
          totalEvents: 0,
          submissionsByBoard: {},
          submissionsByDay: [],
        },
        scannedBoardsCount: 0,
        error: 'אין הרשאת גישה. נדרש אימות מנהל דו-שלבי.',
      };
    }

    const fromDate = new Date(Date.now() - data.days * 24 * 60 * 60 * 1000).toISOString();
    const boardsToScan = data.boardId
      ? MONITORED_BOARDS.filter((b) => b.id === data.boardId)
      : MONITORED_BOARDS;

    const allLogs: NormalizedSecurityLog[] = [];

    await Promise.allSettled(
      boardsToScan.map(async (boardDef) => {
        try {
          const builder = new ActivityLogsBuilder(boardDef.id);
          const rawLogs = await builder.get({
            from: fromDate,
            limit: data.limit,
          });

          for (const raw of rawLogs) {
            allLogs.push(normalizeLog(raw, boardDef.id, boardDef.name));
          }
        } catch (err) {
          console.error(`[ActivityLogs] Failed querying board ${boardDef.name} (${boardDef.id})`, err);
        }
      }),
    );

    // Sort newest first
    allLogs.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    // Compute metrics
    let totalSubmissions = 0;
    let totalSecurityEvents = 0;
    let totalStatusUpdates = 0;
    const submissionsByBoard: Record<string, number> = {};
    const dayMap: Record<string, number> = {};

    for (const log of allLogs) {
      if (log.eventCategory === 'submission') {
        totalSubmissions += 1;
        submissionsByBoard[log.boardName] = (submissionsByBoard[log.boardName] || 0) + 1;
      } else if (log.eventCategory === 'security') {
        totalSecurityEvents += 1;
      } else if (log.eventCategory === 'status_update') {
        totalStatusUpdates += 1;
      }

      const dayKey = log.createdAt.slice(0, 10);
      dayMap[dayKey] = (dayMap[dayKey] || 0) + 1;
    }

    const submissionsByDay = Object.entries(dayMap)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));

    return {
      logs: allLogs.slice(0, data.limit),
      stats: {
        totalSubmissions,
        totalSecurityEvents,
        totalStatusUpdates,
        totalEvents: allLogs.length,
        submissionsByBoard,
        submissionsByDay,
      },
      scannedBoardsCount: boardsToScan.length,
      error: null,
    };
  });
